import React, { useState, useEffect } from 'react';
import { X, Save, MessageSquare, Tag, AlertTriangle, Calendar } from 'lucide-react';

interface Note {
  id: string;
  titre: string;
  contenu: string;
  date: string;
  auteur: string;
  tags: string[];
  priorite: 'haute' | 'moyenne' | 'basse';
}

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  note?: Note | null;
  onSave: (note: Omit<Note, 'id'>) => void;
  mode: 'add' | 'edit';
}

const NoteModal: React.FC<NoteModalProps> = ({ 
  isOpen, 
  onClose, 
  note, 
  onSave, 
  mode 
}) => {
  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    date: new Date().toISOString().split('T')[0],
    auteur: 'Détective Privé',
    tags: [] as string[],
    priorite: 'moyenne' as 'haute' | 'moyenne' | 'basse'
  });
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (note && mode === 'edit') {
      setFormData({
        titre: note.titre,
        contenu: note.contenu,
        date: note.date,
        auteur: note.auteur,
        tags: [...note.tags],
        priorite: note.priorite
      });
    } else {
      setFormData({
        titre: '',
        contenu: '',
        date: new Date().toISOString().split('T')[0],
        auteur: 'Détective Privé',
        tags: [],
        priorite: 'moyenne'
      });
    }
    setErrors({});
  }, [note, mode, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.titre.trim()) {
      newErrors.titre = 'Le titre est requis';
    }
    
    if (!formData.contenu.trim()) {
      newErrors.contenu = 'Le contenu est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const getPrioriteColor = (priorite: string) => {
    switch (priorite) {
      case 'haute': return 'bg-red-600 text-white';
      case 'moyenne': return 'bg-yellow-600 text-white';
      case 'basse': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 w-screen h-screen flex items-center justify-center z-[99999] p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
      <div 
        className="w-full max-w-4xl bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700/50 transform transition-all duration-300 ease-out animate-slideUp" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header moderne */}
        <div className="relative p-8 bg-gradient-to-r from-orange-500/10 to-orange-600/10 border-b border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-1">
                  {mode === 'add' ? 'Nouvelle note' : 'Modifier la note'}
                </h2>
                <p className="text-gray-400">Gestion des notes d'enquête</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 rounded-2xl bg-gray-700/50 hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Titre */}
            <div className="md:col-span-2 relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Titre *</label>
              <input
                type="text"
                value={formData.titre}
                onChange={(e) => setFormData(prev => ({ ...prev, titre: e.target.value }))}
                className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 ${
                  errors.titre 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'
                } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
                placeholder="Titre de la note"
                required
              />
              {errors.titre && (
                <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{errors.titre}</span>
                </div>
              )}
            </div>

            {/* Priorité */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Priorité</label>
              <select
                value={formData.priorite}
                onChange={(e) => setFormData(prev => ({ ...prev, priorite: e.target.value as any }))}
                className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
              >
                <option value="basse">Basse</option>
                <option value="moyenne">Moyenne</option>
                <option value="haute">Haute</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-4 py-4 pl-12 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                />
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
              </div>
            </div>

            {/* Auteur */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Auteur</label>
              <input
                type="text"
                value={formData.auteur}
                onChange={(e) => setFormData(prev => ({ ...prev, auteur: e.target.value }))}
                className="w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                placeholder="Nom de l'auteur"
              />
            </div>
          </div>

          {/* Contenu */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Contenu *</label>
            <textarea
              value={formData.contenu}
              onChange={(e) => setFormData(prev => ({ ...prev, contenu: e.target.value }))}
              className={`w-full px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 transition-all duration-300 resize-none ${
                errors.contenu 
                  ? 'border-red-500/50 focus:border-red-500' 
                  : 'border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50'
              } focus:outline-none focus:ring-4 focus:ring-orange-500/20`}
              placeholder="Contenu détaillé de la note..."
              rows={6}
              required
            />
            {errors.contenu && (
              <div className="flex items-center space-x-2 mt-2 text-red-400 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{errors.contenu}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="relative group">
            <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
            <div className="flex flex-wrap gap-3 mb-4">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-orange-600/20 text-orange-400 text-sm rounded-2xl flex items-center space-x-2 border border-orange-500/30 backdrop-blur-sm"
                >
                  <span>{tag}</span>
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-red-400 transition-colors rounded-full p-1 hover:bg-red-500/20"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex space-x-3">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-4 py-4 bg-gray-800/50 backdrop-blur-sm rounded-2xl text-white placeholder-gray-500 border-2 border-gray-700/50 hover:border-gray-600/50 focus:border-orange-500/50 focus:outline-none focus:ring-4 focus:ring-orange-500/20 transition-all duration-300"
                placeholder="Ajouter un tag"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-2xl text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Tag className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Aperçu de la priorité */}
          <div className="flex items-center space-x-3 p-4 bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700/50">
            <span className="text-sm font-medium text-gray-300">Priorité:</span>
            <span className={`px-3 py-1 rounded-xl text-xs font-medium ${getPrioriteColor(formData.priorite)}`}>
              {formData.priorite.toUpperCase()}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-8 border-t border-gray-700/30">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white rounded-2xl font-medium transition-all duration-300 backdrop-blur-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Save className="w-5 h-5" />
              <span>{mode === 'add' ? 'Créer' : 'Modifier'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoteModal; 